'use client';

import { RefObject, useEffect, useRef } from 'react';
import { VideoType } from 'helpers/Constants/Constant';
import {
  createMilestoneSession,
  sendVideoAnalyticsEvent,
  VideoAnalyticsMeta,
} from 'lib/utils/video-analytics';

const YOUTUBE_API_SCRIPT_ID = 'youtube-iframe-api';
const PROGRESS_POLL_MS = 500;
const VIMEO_ORIGIN_PATTERN = /^https?:\/\/player\.vimeo\.com/i;

type UseVideoAnalyticsOptions = {
  iframeRef: RefObject<HTMLIFrameElement | null>;
  platform: VideoType;
  videoId: string;
  videoTitle: string;
  enabled?: boolean;
};

let youTubeApiPromise: Promise<void> | null = null;

const loadYouTubeIframeApi = (): Promise<void> => {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.YT?.Player) return Promise.resolve();
  if (youTubeApiPromise) return youTubeApiPromise;

  youTubeApiPromise = new Promise((resolve) => {
    const previousCallback = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previousCallback?.();
      resolve();
    };

    if (!document.getElementById(YOUTUBE_API_SCRIPT_ID)) {
      const tag = document.createElement('script');
      tag.id = YOUTUBE_API_SCRIPT_ID;
      tag.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(tag);
    } else if (window.YT?.Player) {
      resolve();
    }
  });

  return youTubeApiPromise;
};

const postVimeoCommand = (
  iframe: HTMLIFrameElement,
  method: string,
  value?: string,
  targetOrigin = '*'
): void => {
  if (!iframe.contentWindow) return;

  const message = value != null ? { method, value } : { method };
  // Vimeo accepts both object and stringified payloads; send both styles safely as string.
  iframe.contentWindow.postMessage(JSON.stringify(message), targetOrigin);
};

/**
 * Tracks video impression, play, pause, completion, and progress milestones
 * via YouTube / Vimeo player APIs and pushes events to GTM.
 */
export const useVideoAnalytics = ({
  iframeRef,
  platform,
  videoId,
  videoTitle,
  enabled = true,
}: UseVideoAnalyticsOptions): void => {
  const metaRef = useRef<VideoAnalyticsMeta>({ videoTitle, videoId });
  metaRef.current = { videoTitle, videoId };

  useEffect(() => {
    if (!enabled || !platform || !videoId) return;

    const iframe = iframeRef.current;
    if (!iframe) return;

    const milestones = createMilestoneSession();
    let hasImpressed = false;
    let hasEnded = false;
    let isPlaying = false;
    let cancelled = false;
    let progressIntervalId: number | null = null;
    let youTubePlayer: YTPlayer | null = null;
    let vimeoOrigin = '*';

    const getMeta = (): VideoAnalyticsMeta => metaRef.current;

    const trackImpression = () => {
      if (hasImpressed) return;
      hasImpressed = true;
      sendVideoAnalyticsEvent('impression', getMeta());
    };

    const trackProgress = (percent: number) => {
      milestones.consume(percent).forEach((milestone) => {
        sendVideoAnalyticsEvent('progress', getMeta(), milestone);
      });
    };

    const stopProgressPolling = () => {
      if (progressIntervalId != null) {
        window.clearInterval(progressIntervalId);
        progressIntervalId = null;
      }
    };

    const startProgressPolling = (getPercent: () => number | null) => {
      stopProgressPolling();
      progressIntervalId = window.setInterval(() => {
        const percent = getPercent();
        if (percent != null) trackProgress(percent);
      }, PROGRESS_POLL_MS);
    };

    const onPlay = () => {
      if (hasEnded) {
        milestones.reset();
        hasEnded = false;
      }
      if (!isPlaying) {
        isPlaying = true;
        sendVideoAnalyticsEvent('play', getMeta());
      }
    };

    const onPause = () => {
      if (!isPlaying) return;
      isPlaying = false;
      sendVideoAnalyticsEvent('pause', getMeta());
    };

    const onComplete = () => {
      isPlaying = false;
      hasEnded = true;
      trackProgress(100);
      sendVideoAnalyticsEvent('complete', getMeta());
    };

    const onIframeLoad = () => {
      trackImpression();
    };

    iframe.addEventListener('load', onIframeLoad);

    let removePlatformListeners: (() => void) | undefined;

    if (platform === 'youtube') {
      loadYouTubeIframeApi().then(() => {
        if (cancelled || !iframeRef.current || !window.YT?.Player) return;

        youTubePlayer = new window.YT.Player(iframeRef.current, {
          events: {
            onReady: () => {
              trackImpression();
            },
            onStateChange: (event) => {
              const { PlayerState } = window.YT!;
              if (event.data === PlayerState.PLAYING) {
                onPlay();
                startProgressPolling(() => {
                  const duration = event.target.getDuration();
                  if (!duration) return null;
                  return (event.target.getCurrentTime() / duration) * 100;
                });
              } else if (event.data === PlayerState.PAUSED) {
                stopProgressPolling();
                onPause();
              } else if (event.data === PlayerState.ENDED) {
                stopProgressPolling();
                onComplete();
              }
              // Keep polling through BUFFERING so progress milestones are not missed
            },
          },
        });
      });

      removePlatformListeners = () => {
        stopProgressPolling();
        try {
          youTubePlayer?.destroy();
        } catch {
          // Player may already be torn down with the iframe
        }
        youTubePlayer = null;
      };
    } else if (platform === 'vimeo') {
      // Native Froogaloop/postMessage protocol (no external script; CSP-safe).
      // Prefer subscribing on `ready`; also retry on iframe load in case `ready`
      // fired before this effect attached the listener.
      const vimeoEvents = [
        'play',
        'pause',
        'finish',
        'ended',
        'playProgress',
        'timeupdate',
        'loaded',
      ] as const;

      const subscribeToVimeoEvents = () => {
        if (cancelled) return;
        vimeoEvents.forEach((eventName) => {
          postVimeoCommand(iframe, 'addEventListener', eventName, vimeoOrigin);
        });
      };

      const onMessage = (event: MessageEvent) => {
        if (cancelled) return;
        if (!VIMEO_ORIGIN_PATTERN.test(String(event.origin))) return;
        if (iframe.contentWindow && event.source !== iframe.contentWindow) return;

        let data: { event?: string; data?: { percent?: number } };
        try {
          data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        } catch {
          return;
        }

        if (!data || typeof data !== 'object' || !data.event) return;

        // Lock origin after first trusted message for subsequent posts
        if (vimeoOrigin === '*') {
          vimeoOrigin = event.origin;
        }

        switch (data.event) {
          case 'ready':
            trackImpression();
            subscribeToVimeoEvents();
            break;
          case 'loaded':
            trackImpression();
            break;
          case 'play':
            onPlay();
            break;
          case 'pause':
            onPause();
            break;
          case 'finish':
          case 'ended':
            onComplete();
            break;
          case 'playProgress':
          case 'timeupdate': {
            const percent = (data.data?.percent ?? 0) * 100;
            trackProgress(percent);
            break;
          }
          default:
            break;
        }
      };

      window.addEventListener('message', onMessage);

      const subscriptionRetryTimeouts: number[] = [];

      const onVimeoIframeLoad = () => {
        trackImpression();
        subscribeToVimeoEvents();
        subscriptionRetryTimeouts.push(
          window.setTimeout(subscribeToVimeoEvents, 250),
          window.setTimeout(subscribeToVimeoEvents, 1000)
        );
      };

      iframe.addEventListener('load', onVimeoIframeLoad);
      // If the iframe already has a src, attempt an immediate subscribe as well
      if (iframe.getAttribute('src')) {
        subscribeToVimeoEvents();
      }

      removePlatformListeners = () => {
        window.removeEventListener('message', onMessage);
        iframe.removeEventListener('load', onVimeoIframeLoad);
        subscriptionRetryTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
      };
    }

    return () => {
      cancelled = true;
      stopProgressPolling();
      iframe.removeEventListener('load', onIframeLoad);
      removePlatformListeners?.();
    };
  }, [enabled, iframeRef, platform, videoId]);
};
